import { t } from "@travily/i18n";
import { Image } from "@ui/components/Image";
import { Page } from "@ui/components/Page";
import { PillTab, PillTabList, PillTabs } from "@ui/components/PillTabs";
import { PreviewFrame, type PreviewViewport } from "@ui/components/PreviewFrame";
import { StandardButton } from "@ui/components/StandardButton";
import { useState } from "react";
import type { PublishStepViewProps } from "./types";

// i18n-tasks-use t('web.products.creator.steps.publish.show.preview_toggle_label')
// i18n-tasks-use t('web.products.creator.steps.publish.show.preview_desktop')
// i18n-tasks-use t('web.products.creator.steps.publish.show.preview_mobile')
// i18n-tasks-use t('web.products.creator.steps.publish.show.preview_sheet_title')
// i18n-tasks-use t('web.products.creator.steps.publish.show.show_preview')
// i18n-tasks-use t('web.products.creator.steps.publish.show.ready_body')
// i18n-tasks-use t('web.products.creator.steps.publish.show.payouts_blocked_title')
// i18n-tasks-use t('web.products.creator.steps.publish.show.payouts_blocked_body')
// i18n-tasks-use t('web.products.creator.steps.publish.show.payouts_pending_title')
// i18n-tasks-use t('web.products.creator.steps.publish.show.payouts_pending_body')
// i18n-tasks-use t('web.products.creator.steps.publish.show.set_up_payouts')
// i18n-tasks-use t('web.products.creator.steps.publish.show.auto_publish_label')
// i18n-tasks-use t('web.services.creator.steps.publish.show.preview_toggle_label')
// i18n-tasks-use t('web.services.creator.steps.publish.show.preview_desktop')
// i18n-tasks-use t('web.services.creator.steps.publish.show.preview_mobile')
// i18n-tasks-use t('web.services.creator.steps.publish.show.preview_sheet_title')
// i18n-tasks-use t('web.services.creator.steps.publish.show.show_preview')
// i18n-tasks-use t('web.services.creator.steps.publish.show.ready_body')
// i18n-tasks-use t('web.services.creator.steps.publish.show.payouts_blocked_title')
// i18n-tasks-use t('web.services.creator.steps.publish.show.payouts_blocked_body')
// i18n-tasks-use t('web.services.creator.steps.publish.show.payouts_pending_title')
// i18n-tasks-use t('web.services.creator.steps.publish.show.payouts_pending_body')
// i18n-tasks-use t('web.services.creator.steps.publish.show.set_up_payouts')
// i18n-tasks-use t('web.services.creator.steps.publish.show.auto_publish_label')

/**
 * Publish-stage body — a review-and-preview screen. The parent page owns the
 * wizard chrome (top bar, progress, back button, primary "Publish" action) and
 * renders this view inside a `Wizard.Stage` body.
 *
 * Desktop shows a Desktop/Mobile toggle over a framed, faithful iframe preview;
 * mobile shows a compact summary card whose "Show preview" button opens the
 * preview full-screen. The payout-gating controls are retained below.
 */
export function PublishStepView({
  kind,
  isFree,
  stripeConnected,
  stripeConnectPending,
  autoPublishOnPayoutsReady,
  onAutoPublishChange,
  onSetUpPayouts,
  previewUrl,
  summary,
}: PublishStepViewProps) {
  const ns = `web.${kind}s.creator.steps.publish.show` as const;
  const blockedOnPayouts = !isFree && !stripeConnected;
  const [viewport, setViewport] = useState<PreviewViewport>("desktop");
  const [previewOpen, setPreviewOpen] = useState(false);
  const previewTitle = t(`${ns}.preview_sheet_title`);

  return (
    <div className="flex w-full max-w-[760px] flex-col gap-6">
      {/* Desktop: segmented Desktop/Mobile toggle over a framed iframe preview. */}
      <div className="hidden flex-col items-center gap-5 md:flex">
        <PillTabs selectedKey={viewport} onSelectionChange={(key) => setViewport(key as PreviewViewport)} size="lg">
          <PillTabList aria-label={t(`${ns}.preview_toggle_label`)}>
            <PillTab id="desktop">{t(`${ns}.preview_desktop`)}</PillTab>
            <PillTab id="mobile">{t(`${ns}.preview_mobile`)}</PillTab>
          </PillTabList>
        </PillTabs>
        <PreviewFrame src={previewUrl} title={previewTitle} viewport={viewport} />
      </div>

      {/* Mobile: a compact card whose "Show preview" button opens the preview full-screen. */}
      <div className="md:hidden">
        <div className="overflow-hidden rounded-2xl border border-neutral-200">
          <div className="relative aspect-[4/3] bg-neutral-100">
            <Image
              alt={summary.title}
              className="size-full object-cover"
              fallback={<div className="size-full bg-neutral-100" />}
              image={summary.coverImage}
              sizes="100vw"
            />
            <StandardButton
              className="absolute top-3 left-3 bg-white shadow-md"
              color="neutral"
              onPress={() => setPreviewOpen(true)}
              size="44"
            >
              {t(`${ns}.show_preview`)}
            </StandardButton>
          </div>
          <div className="flex flex-col gap-1 p-4">
            <p className="font-semibold text-lg text-neutral-900">{summary.title}</p>
            <p className="text-base text-neutral-600">{summary.priceLabel}</p>
          </div>
        </div>
      </div>

      <Page.Root isOpen={previewOpen} onOpenChange={setPreviewOpen}>
        <Page.Content>
          <Page.Header onClose={() => setPreviewOpen(false)} title={previewTitle} />
          <div className="h-[80svh] w-full">
            {/* The full-screen page already provides the intended (device) viewport,
                so the preview fills it edge-to-edge instead of simulating one. */}
            <PreviewFrame src={previewUrl} title={previewTitle} viewport="fill" />
          </div>
        </Page.Content>
      </Page.Root>

      {blockedOnPayouts ? (
        <div className="flex flex-col gap-6">
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-base text-neutral-800">
            <p className="font-semibold">
              {stripeConnectPending ? t(`${ns}.payouts_pending_title`) : t(`${ns}.payouts_blocked_title`)}
            </p>
            <p className="mt-2 text-neutral-700 text-sm">
              {stripeConnectPending ? t(`${ns}.payouts_pending_body`) : t(`${ns}.payouts_blocked_body`)}
            </p>
            {!stripeConnectPending && (
              <div className="mt-4">
                <StandardButton color="pink" size="44" onPress={onSetUpPayouts}>
                  {t(`${ns}.set_up_payouts`)}
                </StandardButton>
              </div>
            )}
          </div>

          <label className="flex items-start gap-3 text-neutral-700 text-sm">
            <input
              type="checkbox"
              checked={autoPublishOnPayoutsReady}
              onChange={(event) => onAutoPublishChange(event.target.checked)}
              className="mt-1 size-4 rounded border-neutral-300 text-pink-600 focus:ring-pink-500"
            />
            <span>{t(`${ns}.auto_publish_label`)}</span>
          </label>
        </div>
      ) : (
        <p className="text-center text-base text-neutral-600">{t(`${ns}.ready_body`)}</p>
      )}
    </div>
  );
}
